import type { ChainId_L1 } from '@cronos-app/sdk';
import {
  ADDRESS_BY_CHAIN_L1,
  ChainLayer,
  YbTokenBridgeTokenType,
} from '@cronos-app/sdk';
import { assertNever } from '@cronos-app/utils';

import type { TokenConfig, TokenContext } from './model';
import { Token } from './model';
import { TOKEN_CONFIG_MAP } from './tokenConfig';

export const getTokenAddressByCtx = (
  desiredNetwork: ChainId_L1,
  ctx: TokenContext,
) => {
  if (ctx.type === Token.Unknown) return ctx.address;
  return TOKEN_CONFIG_MAP[desiredNetwork][ctx.type][
    getTokenAddressKey(ctx.layer)
  ];
};

export const getTokenAddressKey = (chainLayer: ChainLayer) => {
  if (chainLayer === ChainLayer.L1) {
    return 'addressL1';
  }
  if (chainLayer === ChainLayer.L2) {
    return 'addressL2';
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (chainLayer === ChainLayer.CronosEvm) {
    return 'addressCronosEvm';
  }
  assertNever(chainLayer);
};

export const getRelatedYbZkToken = (
  desiredNetwork: ChainId_L1,
  token?: Token,
) => {
  if (!token) return;
  const ybToken = getRelatedYbToken(desiredNetwork, token);
  if (!ybToken) return;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const zkToken = getRelatedZkToken(desiredNetwork, ybToken.ybToken.type)!;
  return {
    ...ybToken,
    zkToken,
  };
};

export const getRelatedZkToken = (desiredNetwork: ChainId_L1, token: Token) => {
  switch (token) {
    case Token.ybETH:
      return TOKEN_CONFIG_MAP[desiredNetwork][Token.vETH];
    case Token.ybUSD:
      return TOKEN_CONFIG_MAP[desiredNetwork][Token.vUSD];
    default:
      return;
  }
};

const getRelatedYbToken = (
  desiredNetwork: ChainId_L1,
  token: Token,
):
  | {
      ybToken: Omit<TokenConfig, 'type'> & {
        type: Token.ybETH | Token.ybUSD;
      };
      ybTokenBridgeTokenType?: YbTokenBridgeTokenType;
    }
  | undefined => {
  let ybTokenBridgeTokenType: YbTokenBridgeTokenType | undefined;
  switch (token) {
    case Token.ETH:
      ybTokenBridgeTokenType = YbTokenBridgeTokenType.ETH;
      break;
    case Token.DAI:
      ybTokenBridgeTokenType = YbTokenBridgeTokenType.DAI;
      break;
    case Token.USDC:
      ybTokenBridgeTokenType = YbTokenBridgeTokenType.USDC;
      break;
    case Token.USDT:
      ybTokenBridgeTokenType = YbTokenBridgeTokenType.USDT;
      break;
    case Token.vETH:
    case Token.vUSD:
      break;

    default:
      return;
  }
  const type =
    token === Token.ETH || token === Token.vETH ? Token.ybETH : Token.ybUSD;
  const ybToken = TOKEN_CONFIG_MAP[desiredNetwork][type];
  return {
    ybToken: {
      ...ybToken,
      type,
    },
    ybTokenBridgeTokenType,
  };
};

export const getYbTokenBridgeAddress = (
  chainId: ChainId_L1,
  depositToken: TokenConfig,
) => {
  const ybToken = getRelatedYbZkToken(chainId, depositToken.type)?.ybToken;
  return ybToken?.type === Token.ybETH
    ? ADDRESS_BY_CHAIN_L1[chainId].YbETHBridge
    : ADDRESS_BY_CHAIN_L1[chainId].YbUSDBridge;
};
